import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthApiService, CampaignApiService } from '@digilize/shared/data/services';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { getJwtExpiration } from '@digilize/shared/utils/helpers';
import { APP_CONFIG, WINDOW } from '@digilize/shared/utils/tokens';
import { AppConfig, PossibleApiPrefix, Token } from '@shared/definitions';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  DialogLostSessionComponent,
  DialogLostSessionComponentData,
} from './dialog-lost-session/dialog-lost-session.component';

const MS_PER_MINUTE = 60 * 1000;

/** How long renewals pause after a 429 when the server sends no usable `Retry-After`. */
const RENEWAL_PAUSE_AFTER_429_MS = 30 * 1000;
const MAX_RENEWAL_PAUSE_MS = 5 * MS_PER_MINUTE;
/** Consecutive throttled renewals after which the session is treated as lost (PAUL-3928). */
const MAX_THROTTLED_RENEWALS = 3;

/**
 * Epoch milliseconds `minutes` from now — the only thing this file ever asked moment for, alongside
 * `moment.now()`. Both are plain arithmetic, and importing moment for them put 141 kB in the initial
 * bundle of every app that authenticates.
 */
const minutesFromNow = (minutes: number): number => Date.now() + minutes * MS_PER_MINUTE;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly REFRESH_TOKEN_EXPIRATION = 60 * 24 * 365 * 100; // In minutes (100years)
  private USER_INTERACTED_WITH_APP = false;
  /** The open lost-session dialog, so every request refused after it adds nothing on top of it. */
  private lostSessionDialog: MatDialogRef<unknown> | null = null;
  /** Per backend: no renewal is sent before this epoch ms, after the server answered 429. */
  private renewalPausedUntil: Partial<Record<PossibleApiPrefix, number>> = {};
  private throttledRenewals: Partial<Record<PossibleApiPrefix, number>> = {};

  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private authApiService: AuthApiService,
    private cookieService: CookieService,
    private dialogService: DialogService,
    private router: Router,
    private campaignApiService: CampaignApiService,
    private snackbarService: SnackbarService,
    @Inject(WINDOW) private window: Window
  ) {
    this.window.addEventListener('click', () => {
      this.USER_INTERACTED_WITH_APP = true;
    });
  }

  isLoggedIn(): boolean {
    return !!this.getRefreshToken();
  }

  getAccessToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix));
    if (Number(tokenExpiration) > Date.now()) {
      return token;
    }
    return null;
  }

  getRefreshToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix));

    if (Number(tokenExpiration) > Date.now()) {
      return token;
    }

    return null;
  }

  logout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      this.window.location.href = logoutRoute;
    }
  }

  softLogout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      this.router.navigate([logoutRoute]);
    }
  }

  refreshAccessToken(apiPrefix: PossibleApiPrefix = 'api'): Observable<null | Token> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (!refreshExpiration || Number(refreshExpiration) <= Date.now()) {
      return of(null);
    }

    /* Inside a 429 pause the answer is already known; asking again only keeps the limiter hot. */
    if ((this.renewalPausedUntil[apiPrefix] ?? 0) > Date.now()) {
      return throwError(() => new HttpErrorResponse({ status: 429, statusText: 'Too Many Requests' }));
    }

    return this.authApiService.refreshToken(token, apiPrefix).pipe(
      tap((newToken: Token) => {
        delete this.renewalPausedUntil[apiPrefix];
        delete this.throttledRenewals[apiPrefix];
        this.setSession(newToken, null, apiPrefix);
      }),
      catchError((error) => {
        if (error?.status === 429) {
          this.onThrottledRenewal(error, apiPrefix);
          return throwError(() => error);
        }

        /* Tabs share one refresh token through localStorage and the server rotates it on use, so a tab
           that loses the race gets a 400 for a session another tab has just renewed. That tab's result is
           already stored: carry on with it rather than ending everyone's session. */
        const stored = this.getToken(apiPrefix).token;
        if (stored.refresh_token && stored.refresh_token !== token.refresh_token) {
          return of(stored);
        }

        this.endSessionAfterFailedRenewal();
        return throwError(() => error);
      })
    );
  }

  /**
   * A 429 is weather, not a verdict: the allowance is spent and nobody has said the session is bad, so a
   * single one keeps the session. Renewals pause until the server's `Retry-After`, and only when the
   * server keeps refusing after that is the session given up — otherwise every click fails behind the
   * limiter with no way out (PAUL-3928).
   */
  private onThrottledRenewal(error: HttpErrorResponse, apiPrefix: PossibleApiPrefix): void {
    const strikes = (this.throttledRenewals[apiPrefix] ?? 0) + 1;

    if (strikes >= MAX_THROTTLED_RENEWALS) {
      delete this.renewalPausedUntil[apiPrefix];
      delete this.throttledRenewals[apiPrefix];
      this.endSessionAfterFailedRenewal();
      return;
    }

    this.throttledRenewals[apiPrefix] = strikes;
    this.renewalPausedUntil[apiPrefix] = Date.now() + this.retryAfterMs(error);
    this.snackbarService.openSnackbarWithApiError(error?.error?.error, 20000);
  }

  private retryAfterMs(error: HttpErrorResponse): number {
    const seconds = Number(error?.headers?.get?.('Retry-After'));

    return seconds > 0 ? Math.min(seconds * 1000, MAX_RENEWAL_PAUSE_MS) : RENEWAL_PAUSE_AFTER_429_MS;
  }

  /**
   * Ends the session after a renewal the server actually refused, and routes to login.
   *
   * The session is cleared here, not when the dialog closes: a refresh token the server has rejected
   * never becomes good again, and while it stayed in storage every later request — pollers included —
   * renewed with it again, got refused again and stacked another dialog, until the server rate-limited
   * the tab for hours (PAUL-3928). With it gone, later requests go out unauthenticated instead of renewing.
   *
   * Both branches end in the same place; the dialog is only there to explain the jump to somebody who
   * was mid-task, and is opened once. `USER_INTERACTED_WITH_APP` resets on every page load, so a renewal
   * that fails before the first click takes the quiet branch.
   *
   * There is deliberately no `window.location.reload()` after `logout()` (PAUL-3246): it made the page
   * look like it crashed, and only raced the navigation `logout()` already starts.
   */
  private endSessionAfterFailedRenewal(): void {
    const isAdminPanel = this.window.location.href.includes('/admin');

    if (isAdminPanel && this.USER_INTERACTED_WITH_APP) {
      const returnTo = this.router.url;
      this.removeSession();

      if (this.lostSessionDialog) {
        return;
      }

      this.lostSessionDialog = this.dialogService.openDialog(DialogLostSessionComponent);
      const canReturn = returnTo && returnTo !== '/' && !returnTo.startsWith('/auth');
      this.lostSessionDialog.afterClosed().subscribe(() => {
        this.lostSessionDialog = null;
        this.router.navigate(['/auth'], canReturn ? { state: { afterLoginRoute: returnTo } } : {});
      });

      return;
    }

    this.logout();
  }

  refreshAccessTokenManualRetry(apiPrefix: PossibleApiPrefix = 'api'): Observable<Token | null> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (refreshExpiration && Number(refreshExpiration) > Date.now()) {
      return this.authApiService.refreshToken(token, apiPrefix).pipe(
        tap({
          next: (newToken: Token) => {
            this.setSession(newToken, null, apiPrefix);
          },
          error: (error) => {
            this.dialogService.openDialog<DialogLostSessionComponentData>(DialogLostSessionComponent, {
              data: { closeable: false },
              disableClose: true,
            });
            if (error.status === 429) {
              this.snackbarService.openSnackbarWithApiError(error.error.error, 20000);
              return of(null);
            }
          },
        })
      );
    }

    return of(null);
  }

  setSession(token: Token, callback = null, apiPrefix: PossibleApiPrefix = 'api'): void {
    const refreshTokenExpiration = minutesFromNow(this.REFRESH_TOKEN_EXPIRATION);

    if (token.token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix), token.token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix),
        JSON.stringify(this.resolveTokenExpiration(token.token, apiPrefix))
      );
    }

    if (token.refresh_token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix), token.refresh_token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix),
        JSON.stringify(refreshTokenExpiration)
      );
    }
    this.authApiService.getMe();

    // Claim cart session
    if (callback) {
      const claimCallback = (): void => {
        if (localStorage.getItem('checkout_return')) {
          const returnUrl = localStorage.getItem('checkout_return');
          localStorage.removeItem('checkout_return');
          this.window.location.href = returnUrl;
        } else {
          callback();
        }
      };

      const sessionId = this.cookieService.get('session_id');
      if (sessionId) {
        this.authApiService.claimCartSession(sessionId).subscribe({
          next: () => claimCallback(),
          error: () => claimCallback(),
        });
      } else {
        claimCallback();
      }
    }
  }

  setAcquisitionSession(token: Token, redirectUrl: string, returnUrl: string, app: string): void {
    this.setSession(token, () => {
      const acquisitionId = this.cookieService.get('acquisition_id');
      if (acquisitionId) {
        this.registerAcquisition(acquisitionId, redirectUrl, returnUrl, app);
      } else {
        this.window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/auth'
            : decodeURIComponent(redirectUrl) + returnUrl;
      }
    });
  }

  private registerAcquisition(id: string, redirectUrl?: string, returnUrl?: string, app?: string): void {
    this.campaignApiService.registerAcquisition(id).subscribe({
      next: () => {
        this.cookieService.delete('acquisition_id');
        this.window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/c/0/shops'
            : decodeURIComponent(redirectUrl) + returnUrl;
      },
      error: (error) => {
        if (error?.status === 400) {
          this.cookieService.delete('acquisition_id');
        }

        if (error.error?.error?.info?.kind === 'already_exists') {
          this.window.location.href =
            app === 'dashboard' || this.appConfig.app === 'flixit'
              ? '/admin/c/0/shops'
              : decodeURIComponent(redirectUrl) + returnUrl;
        } else {
          this.router.navigate(['niet-gevonden']);
        }
      },
    });
  }

  /**
   * When the stored access token should be treated as due for renewal, as a millisecond epoch.
   *
   * The token's own `exp` where the app asks for it and the token can be read, and the app's configured
   * lifetime otherwise. Nothing here is a constant: an expiry the frontend invented, unrelated to what
   * the server stamped, is what PAUL-3246 was — 60 seconds against a token good for five, renewing four
   * times more often than needed and burning through the account's session slots.
   *
   * The fallback is what keeps the non-Pauly apps working. Several of their backends issue tokens that
   * are not JWTs at all, so `getJwtExpiration` answers `null` and the configured value decides.
   */
  private resolveTokenExpiration(token: string, apiPrefix: PossibleApiPrefix): number {
    const { source, minutes, minutes2 } = this.appConfig.tokenExpiration;
    const configured = apiPrefix === 'api2' ? (minutes2 ?? minutes) : minutes;

    if (source === 'jwt') {
      const fromToken = getJwtExpiration(token);

      if (fromToken !== null) {
        return fromToken;
      }
    }

    return minutesFromNow(configured);
  }

  private patchWithAppLocalAuthPrefix(localStorageKey: string, apiPrefix: PossibleApiPrefix = 'api'): string {
    /* to distinguish localstorage keys in certain apps (when deployed on same domain apps can share localstorage)  */
    switch (apiPrefix) {
      case 'api':
        const prefix = this.appConfig.localStorageAuthPrefix || '';
        return `${prefix}${localStorageKey}`;
      case 'api2':
        const prefix2 = this.appConfig.localStorageAuthPrefix2 || '';
        return `${prefix2}${localStorageKey}`;
      default:
        throw new Error(`${apiPrefix} is not handled in patchWithAppLocalAuthPrefix`);
    }
  }

  private removeSession(): void {
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('tokenExpiration'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refresh_token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration'));
    this.cookieService.delete('session_id', '/');
    this.authApiService.setMe(null);
  }

  private getToken(apiPrefix: PossibleApiPrefix = 'api'): { token: Token; refreshExpiration: string } {
    const token: Token = {
      token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix)),
      refresh_token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix)),
    };
    const refreshExpiration = localStorage.getItem(
      this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix)
    );

    return { token, refreshExpiration };
  }
}
